import { create } from 'zustand';
import { User } from "@/contexts/AuthContext";
import { getApiUrl } from "@/utils/apiConfig";

const API_URL = getApiUrl();

export interface CitizenData extends User {
  registeredAt: string;
  status?: 'valide' | 'bloque';
  isAssociationMember?: boolean;
  associativeName?: string;
}

interface CitizensState {
  citizens: CitizenData[];
  isLoading: boolean;
  fetchCitizens: () => Promise<void>;
  updateCitizenStatus: (id: string, status: 'valide' | 'bloque') => Promise<void>;
  deleteCitizen: (id: string) => Promise<void>;
}

const getAuthToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('kaynin_auth_token');
  }
  return null;
};

export const useCitizensStore = create<CitizensState>((set, get) => ({
  citizens: [],
  isLoading: false,

  fetchCitizens: async () => {
    set({ isLoading: true });
    try {
      const token = getAuthToken();
      if (!token) throw new Error("Non authentifié");

      const res = await fetch(`${API_URL}/citizens.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        set({ citizens: Array.isArray(data) ? data : [] });
      } else {
        console.error("Failed to fetch citizens");
      }
    } catch (e) {
      console.error("Error fetching citizens", e);
    } finally {
      set({ isLoading: false });
    }
  },

  updateCitizenStatus: async (id, status) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await fetch(`${API_URL}/citizens.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'updateStatus', id, status })
      });

      if (res.ok) {
        set(state => ({
          citizens: state.citizens.map(c => c.id === id ? { ...c, status } : c)
        }));
      }
    } catch (e) {
      console.error("Error updating citizen status", e);
    }
  },

  deleteCitizen: async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await fetch(`${API_URL}/citizens.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'delete', id })
      });

      if (res.ok) {
        set(state => ({
          citizens: state.citizens.filter(c => c.id !== id)
        }));
      }
    } catch (e) {
      console.error("Error deleting citizen", e);
    }
  }
}));
